import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { deletePost, getUserFeed, getSavedPost, getMyPost, getUserPost, savePost, unsavePost } from "./postAction";
import apiInstance from "../../config/api";
import { toast } from "react-toastify";
import { updatePostCount } from "../user/userReducer";

function blobUrlToFile(blobUrl, filename) {
    return fetch(blobUrl)
        .then(response => response.blob())
        .then(blob => new File([blob], filename, { type: blob.type }));
}

export const createPost = createAsyncThunk('post/create', async ({ caption, files, type, backgroundColour, medias, user }, { dispatch }) => {
    const uploadId = `post-uploads-${Date.now()}-${Math.random()}`
    try {
        if (files && files.length > 0) {
            const previewData = { caption, medias: medias }
            dispatch(addUpload({ ...previewData, id: uploadId }))
        }

        const formData = new FormData()
        if (files && files.length > 0) {
            for (let index = 0; index < files.length; index++) {
                const file = files[index];
                const processedImage = await blobUrlToFile(file, `postMedia_${index + 1}.png`);
                // console.log('images ', processedImage)

                formData.append('files', processedImage, processedImage.name)
            }
        }
        formData.append('caption', caption)
        formData.append('backgroundColour', backgroundColour)
        formData.append('type', type)

        const request = await apiInstance.post("/files/api/uploads", formData, {
            onUploadProgress: files?.length > 0 ? (progressEvent) => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                dispatch(setUploadProgress({ id: uploadId, progress: percent }));
            } : undefined,
        })
        const response = request.data
        if (response.status === 'success') {
            console.log('post response ', response)
            if (files && files.length > 0) {
                dispatch(finishUpload({ id: uploadId }))
            }
            dispatch(updatePostCount({ actionType: "add" }))
            return { ...response.uploads, user }
        } else {
            throw new Error(response.message)
        }
    }
    catch (error) {
        toast.error('Error uploading post')
        if (files && files.length > 0) {
            dispatch(finishUpload({ id: uploadId }))
        }
        throw new Error(error.message)
    }
})



const initialState = {
    pagination: {},
    uploads: [],

    loading: false,

    viewPost: {},


    uploading: false,
    // deleting: false,

    deleting: [],

    error: null,


    myPostLoading: false,
    myPost: [],

    userPostLoading: false,
    userPost: [],

    savedPostLoading: false,
    savedPosts: [],
    savedError: null,

    feedLoading: false,
    feed: [],
    feedError: null,
}

const postSlice = createSlice({
    name: 'post',
    initialState,
    reducers: {
        setViewPostIndex: (state, action) => {
            state.viewPostIndex = action.payload
        },

        setViewPost: (state, action) => {
            state.viewPost = action.payload
        },

        addUpload(state, action) {
            const { caption, medias, id } = action.payload
            state.uploads.push({
                id: id,
                medias: medias,
                caption: caption,
                progress: 0,
                uploading: true,
            });
        },
        setUploadProgress(state, action) {
            const upload = state.uploads.find((u) => u.id === action.payload.id);
            if (upload) {
                upload.progress = action.payload.progress;
            }
        },
        finishUpload(state, action) {
            state.uploads = state.uploads.filter((u) => u.id !== action.payload.id);
        },

        deleteingPost(state, action) {
            const { postId } = action.payload
            state.deleting.push(postId)
        },

        // Keeps every post list (main feed, profile posts, saved posts) in
        // sync with the comment count shown/updated inside the ViewPost
        // comment modal, since that modal keeps its own local commentTotal
        // state instead of writing back to redux.
        updatePostCommentCount(state, action) {
            const { postId, commentCount } = action.payload
            const lists = ['feed', 'myPost', 'userPost', 'savedPosts']
            lists.forEach((key) => {
                const index = state[key].findIndex((post) => post.postId === postId)
                if (index !== -1) {
                    state[key][index].commentCount = commentCount
                }
            })
        }
    },

    extraReducers: (builder) => {

        builder
            .addCase(createPost.pending, (state) => {
                state.uploading = true
            })
            .addCase(createPost.fulfilled, (state, action) => {
                state.uploading = false
                state.uploadStatus = 'success'
                state.feed.unshift(action.payload)
                state.myPost.unshift(action.payload)
            })
            .addCase(createPost.rejected, (state, action) => {
                state.uploading = false
                state.uploadStatus = 'failed'
                state.error = action.error.message
            })

        builder
            .addCase(deletePost.fulfilled, (state, action) => {
                state.deleting = state.deleting.filter(id => id !== action.payload)
                const filteredFeed = state.feed.filter(post => post.postId !== action.payload)
                state.feed = filteredFeed
                const filteredMyPost = state.myPost.filter(post => post.postId !== action.payload)
                state.myPost = filteredMyPost
                const filteredSavedPost = state.savedPosts.filter(post => post.postId !== action.payload)
                state.savedPosts = filteredSavedPost
            })
            .addCase(deletePost.rejected, (state, action) => {
                state.deleting = state.deleting.filter(id => id !== action.payload)
                state.error = action.error.message
            })

        builder
            .addCase(getUserFeed.pending, (state) => {
                state.feedError = null
                state.feedLoading = true
            })
            .addCase(getUserFeed.fulfilled, (state, action) => {
                state.feedLoading = false
                const { posts = [], pagination, refresh } = action.payload ?? {};
                const { currentPage } = pagination

                // Step 1: Remove duplicates *inside* the incoming posts
                const dedupedPostsMap = new Map();
                for (const post of posts) {
                    if (!dedupedPostsMap.has(post.postId)) {
                        dedupedPostsMap.set(post.postId, post);
                    }
                }
                const uniquePosts = Array.from(dedupedPostsMap.values());

                // Step 2: Merge with existing feed depending on the page
                if (currentPage === 1 || refresh) {
                    state.feed = uniquePosts;
                } else {
                    const existingIds = new Set(state.feed.map((p) => p.postId));
                    const newPosts = uniquePosts.filter((p) => !existingIds.has(p.postId));
                    state.feed = [...state.feed, ...newPosts];
                }

                // Step 3: Update pagination info
                state.pagination = pagination

            })
            .addCase(getUserFeed.rejected, (state, action) => {
                state.feedLoading = false
                state.feedError = action.error.message
            })

        builder
            .addCase(getMyPost.pending, (state) => {
                state.myPostLoading = true
                state.myPost = []
            })
            .addCase(getMyPost.fulfilled, (state, action) => {
                state.myPostLoading = false
                state.myPost = action.payload
            })
            .addCase(getMyPost.rejected, (state, action) => {
                state.myPostLoading = false
                state.error = action.error.message
            })

        builder
            .addCase(getUserPost.pending, (state) => {
                state.userPostLoading = true
                state.userPost = []
            })
            .addCase(getUserPost.fulfilled, (state, action) => {
                state.userPostLoading = false
                state.userPost = action.payload
            })
            .addCase(getUserPost.rejected, (state, action) => {
                state.userPostLoading = false
                state.error = action.error.message
            })

        builder
            .addCase(savePost.fulfilled, (state, action) => {
                const { isSaved, post } = action.payload
                const postId = post.postId
                if (isSaved) {
                    const index = state.feed.findIndex(post => post.postId === postId)
                    if (index !== -1) {
                        state.feed[index] = {
                            ...state.feed[index],
                            saved: true
                        }
                    }
                    state.savedPosts.unshift(post)
                }
            })

        builder
            .addCase(unsavePost.fulfilled, (state, action) => {
                const { postId, unSaved } = action.payload
                if (unSaved) {
                    const index = state.feed.findIndex(post => post.postId === postId)
                    if (index !== -1) {
                        state.feed[index] = {
                            ...state.feed[index],
                            saved: false
                        }
                    }
                    const filteredSavedPost = state.savedPosts.filter(post => post.postId !== postId)
                    state.savedPosts = filteredSavedPost
                }
            })

        builder
            .addCase(getSavedPost.pending, (state) => {
                state.savedPostLoading = true
            })
            .addCase(getSavedPost.fulfilled, (state, action) => {
                state.savedPostLoading = false
                state.savedPosts = action.payload
            })
            .addCase(getSavedPost.rejected, (state, action) => {
                state.savedPostLoading = false
                state.error = action.error.message
            })
    }
})


export const { setViewPost, addUpload, setUploadProgress, finishUpload, deleteingPost, updatePostCommentCount } = postSlice.actions
export default postSlice.reducer